export const FEEDBACK_STATES = ["open", "future", "wontfix", "obsolete", "fixed", "snoozed"] as const;
export type FeedbackState = typeof FEEDBACK_STATES[number];
export const FEEDBACK_LABELS: Record<FeedbackState, string> = { open: "Open", future: "Future", wontfix: "Won't fix", obsolete: "Obsolete", fixed: "Fixed", snoozed: "Snoozed" };
export function feedbackDisposition(value: string): FeedbackState | null {
	if (FEEDBACK_STATES.includes(value as FeedbackState)) return value as FeedbackState;
	switch (value) {
		case "pending": return "open";
		case "reviewed": case "actioned": return "snoozed";
		case "deferred": return "future";
		case "no-change": return "wontfix";
		default: return null;
	}
}
export type FeedbackKind = "screenshot" | "crash";

export interface AppleResource {
	id: string;
	type: string;
	attributes?: Record<string, unknown>;
	relationships?: Record<string, { data?: { id: string; type: string } | null }>;
}

export interface FeedbackOriginal {
	id: string;
	kind: FeedbackKind;
	createdAt: string | null;
	comment: string | null;
	metadata: Record<string, unknown>;
	build: Record<string, unknown> | null;
	tester: Record<string, unknown> | null;
	screenshots: Array<{ url: string; width: number | null; height: number | null }>;
	resource: AppleResource;
}

export interface FeedbackReview {
	state: FeedbackState;
	findings: string;
	references: string;
	expectedRevision: number;
}

export function feedbackReview(value: unknown): FeedbackReview | null {
	if (!value || typeof value !== "object") return null;
	const input = value as Record<string, unknown>;
	const state = typeof input.state === "string" ? feedbackDisposition(input.state) : null;
	if (!state || typeof input.findings !== "string" ||
		typeof input.references !== "string" || input.findings.length > 20000 || input.references.length > 4000 ||
		!Number.isSafeInteger(input.expectedRevision) || Number(input.expectedRevision) < 0) return null;
	return { state, findings: input.findings, references: input.references, expectedRevision: Number(input.expectedRevision) };
}

export function appleApiUrl(value: string, expectedPath: string): string {
	const url = new URL(value, "https://api.appstoreconnect.apple.com");
	if (url.origin !== "https://api.appstoreconnect.apple.com" || url.username || url.password ||
		url.pathname !== expectedPath || url.hash) throw new Error("Unexpected Apple API URL");
	return url.href;
}

export function appleAttachmentUrl(value: string): string {
	const url = new URL(value);
	if (url.protocol !== "https:" || url.hostname !== "tf-feedback.itunes.apple.com" || url.port ||
		url.username || url.password || url.hash) throw new Error("Unexpected Apple attachment URL");
	return url.href;
}

function record(value: unknown): Record<string, unknown> {
	return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

export function parseFeedbackPage(value: unknown, kind: FeedbackKind, bundleId: string): { items: FeedbackOriginal[]; next: string | null } {
	const page = record(value);
	if (!Array.isArray(page.data)) throw new Error("Invalid Apple feedback page");
	const included = Array.isArray(page.included) ? page.included.map(record) : [];
	const expectedType = kind === "screenshot" ? "betaFeedbackScreenshotSubmissions" : "betaFeedbackCrashSubmissions";
	const items = page.data.map((entry): FeedbackOriginal => {
		const raw = record(entry);
		if (typeof raw.id !== "string" || !/^[A-Za-z0-9_-]{1,128}$/.test(raw.id) || raw.type !== expectedType) throw new Error("Invalid Apple feedback resource");
		const attributes = record(raw.attributes);
		if (attributes.buildBundleId != null && attributes.buildBundleId !== bundleId) throw new Error("Feedback belongs to another app");
		const relationships = record(raw.relationships);
		const related = (name: string): Record<string, unknown> | null => {
			const identity = record(record(relationships[name]).data);
			const resource = included.find(candidate => candidate.id === identity.id && candidate.type === identity.type);
			return resource ? record(resource.attributes) : null;
		};
		const { screenshots: rawScreenshots, ...metadata } = attributes;
		const screenshots = (Array.isArray(rawScreenshots) ? rawScreenshots : []).map(entry => {
			const image = record(entry);
			if (typeof image.url !== "string") throw new Error("Missing Apple screenshot URL");
			return { url: appleAttachmentUrl(image.url), width: typeof image.width === "number" ? image.width : null, height: typeof image.height === "number" ? image.height : null };
		});
		return { id: raw.id, kind, createdAt: typeof attributes.createdDate === "string" ? attributes.createdDate : null,
			comment: typeof attributes.comment === "string" ? attributes.comment : null, metadata, build: related("build"),
			tester: related("tester"), screenshots, resource: { id: raw.id, type: expectedType, attributes, relationships: raw.relationships as AppleResource["relationships"] } };
	});
	const next = record(page.links).next;
	if (next != null && typeof next !== "string") throw new Error("Invalid Apple pagination link");
	return { items, next: typeof next === "string" && next ? next : null };
}

function base64url(bytes: Uint8Array): string {
	return btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join("")).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function appStoreToken(privateKeyPem: string, keyId: string, issuerId: string, nowMs = Date.now()): Promise<string> {
	const encoder = new TextEncoder();
	const encode = (value: unknown) => base64url(encoder.encode(JSON.stringify(value)));
	const seconds = Math.floor(nowMs / 1000);
	const input = `${encode({ alg: "ES256", kid: keyId, typ: "JWT" })}.${encode({ iss: issuerId, iat: seconds, exp: seconds + 300, aud: "appstoreconnect-v1" })}`;
	const pem = privateKeyPem.replace(/\\n/g, "\n").replace(/-----[^-]+-----/g, "").replace(/\s/g, "");
	const key = await crypto.subtle.importKey("pkcs8", Uint8Array.from(atob(pem), char => char.charCodeAt(0)), { name: "ECDSA", namedCurve: "P-256" }, false, ["sign"]);
	const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, key, encoder.encode(input));
	return `${input}.${base64url(new Uint8Array(signature))}`;
}

export async function verifyAppleWebhook(body: Uint8Array, signature: string | null, secret: string): Promise<boolean> {
	if (!signature || !/^hmacsha256=[a-f0-9]{64}$/.test(signature) || !secret) return false;
	const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["verify"]);
	const bytes = Uint8Array.from(signature.slice(11).match(/../g) ?? [], hex => parseInt(hex, 16));
	return crypto.subtle.verify("HMAC", key, bytes, body as BufferSource);
}

export function feedbackWebhook(value: unknown): { id: string; kind: FeedbackKind; submissionId: string } | null {
	const data = record(record(value).data);
	const kind = data.type === "betaFeedbackScreenshotSubmissionCreated" ? "screenshot" : data.type === "betaFeedbackCrashSubmissionCreated" ? "crash" : null;
	const instance = record(record(record(data.relationships).instance).data);
	if (!kind || typeof data.id !== "string" || !/^[A-Za-z0-9_-]{1,128}$/.test(data.id) ||
		typeof instance.id !== "string" || !/^[A-Za-z0-9_-]{1,128}$/.test(instance.id) ||
		instance.type !== (kind === "screenshot" ? "betaFeedbackScreenshotSubmissions" : "betaFeedbackCrashSubmissions")) return null;
	return { id: data.id, kind, submissionId: instance.id };
}

export async function boundedBytes(response: Response | Request, limit: number): Promise<Uint8Array> {
	if (!response.body) return new Uint8Array();
	const reader = response.body.getReader();
	const chunks: Uint8Array[] = [];
	let length = 0;
	try {
		while (true) {
			const part = await reader.read();
			if (part.done) break;
			length += part.value.length;
			if (length > limit) { await reader.cancel(); throw new Error("Payload too large"); }
			chunks.push(part.value);
		}
	} finally { reader.releaseLock(); }
	const output = new Uint8Array(length);
	let offset = 0;
	for (const chunk of chunks) { output.set(chunk, offset); offset += chunk.length; }
	return output;
}